import React, { useState, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, Printer, Check, AlertCircle } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface ContractData {
  contractNumber: string;
  date: string;
  location: string;
  client: {
    companyName: string;
    representedBy: string;
    title: string;
  };
  formule: {
    type: 'audit' | 'standard' | 'premium' | 'signature';
    commitment?: 6 | 12; // Pas d'engagement pour audit
  };
  loyaltyDiscount: boolean;
  auditComplet?: {
    included: boolean;
    offered: boolean;
  };
}

export const ContractExpress: React.FC = () => {
  const documentRef = useRef<HTMLDivElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [contractData, setContractData] = useState<ContractData>({
    contractNumber: `CE-${Date.now().toString().slice(-6)}`,
    date: new Date().toISOString().split('T')[0],
    location: 'Paris',
    client: {
      companyName: '',
      representedBy: '',
      title: 'Gérant(e)'
    },
    formule: {
      type: 'premium',
      commitment: 6
    },
    loyaltyDiscount: false,
    auditComplet: {
      included: false,
      offered: false
    }
  });

  const FORMULES = {
    audit: {
      name: 'Audit 360° Complet',
      price: 4000,
      type: 'forfait',
      payment: '30 % à la signature, 70 % à la livraison',
      commitment: '—',
      loyalty: '—'
    },
    standard: {
      name: 'Standard',
      price: 1000,
      type: 'mensuel',
      payment: (commitment: number) => commitment === 6 ? '1er mois' : '2 mois',
      commitment: '6 ou 12 mois',
      loyalty: '–15 % sur 12 mois'
    },
    premium: {
      name: 'Premium',
      price: 2000,
      type: 'mensuel',
      payment: (commitment: number) => commitment === 6 ? '1er mois' : '2 mois',
      commitment: '6 ou 12 mois',
      loyalty: '–15 % sur 12 mois'
    },
    signature: {
      name: 'Signature',
      price: 3500,
      type: 'mensuel',
      payment: (commitment: number) => commitment === 6 ? '1er mois' : '2 mois',
      commitment: '6 ou 12 mois',
      loyalty: '–15 % sur 12 mois'
    }
  };

  const handleDownloadPDF = async () => {
    if (!documentRef.current || isGenerating) return;
    
    setIsGenerating(true);
    
    try {
      const element = documentRef.current;
      
      const canvas = await html2canvas(element, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: 1200,
      });

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: 'a4',
        hotfixes: ['px_scaling']
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
      
      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 0;
      const totalPages = Math.ceil(imgHeight / pdfHeight);

      while (heightLeft > 0) {
        if (pageNumber > 0) {
          pdf.addPage();
        }
        
        pdf.addImage(
          canvas.toDataURL('image/png'),
          'PNG',
          0,
          position,
          imgWidth,
          imgHeight,
          undefined,
          'FAST'
        );
        
        // Ajouter numéro de page
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(
          `Page ${pageNumber + 1} / ${totalPages}`,
          pdfWidth / 2,
          pdfHeight - 20,
          { align: 'center' }
        );
        
        heightLeft -= pdfHeight;
        position -= pdfHeight;
        pageNumber++;
      }

      const fileName = contractData.client.companyName 
        ? `Contrat_Express_STRATOS_${contractData.client.companyName.replace(/\s+/g, '_')}.pdf`
        : `Contrat_Express_STRATOS_${contractData.contractNumber}.pdf`;
      
      pdf.save(fileName);
      
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      alert('Erreur lors de la génération du PDF. Veuillez réessayer.');
    } finally {
      setIsGenerating(false);
    }
  };

  const calculateMonthlyPrice = () => {
    const formule = FORMULES[contractData.formule.type];
    if (formule.type === 'forfait') return { monthlyHT: 0, totalHT: formule.price };
    
    const basePrice = formule.price;
    const discount = contractData.loyaltyDiscount && contractData.formule.commitment === 12 ? 0.15 : 0;
    const monthlyHT = basePrice * (1 - discount);
    const totalHT = monthlyHT * (contractData.formule.commitment || 6);
    
    return { monthlyHT, totalHT, discount };
  };

  const pricing = calculateMonthlyPrice();

  return (
    <div className="space-y-6">
      {/* Form Section */}
      <Card className="p-6 print:hidden">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          Contrat Express de Prestation (Version 2025)
        </h2>
        
        <div className="space-y-6">
          {/* Client Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Raison sociale *"
              value={contractData.client.companyName}
              onChange={(e) => setContractData(prev => ({
                ...prev,
                client: { ...prev.client, companyName: e.target.value }
              }))}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stratos-gold"
            />
            <input
              type="text"
              placeholder="Représenté par *"
              value={contractData.client.representedBy}
              onChange={(e) => setContractData(prev => ({
                ...prev,
                client: { ...prev.client, representedBy: e.target.value }
              }))}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stratos-gold"
            />
          </div>

          {/* Formule Selection */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <select
              value={contractData.formule.type}
              onChange={(e) => {
                const type = e.target.value as 'audit' | 'standard' | 'premium' | 'signature';
                setContractData(prev => ({
                  ...prev,
                  formule: { 
                    type, 
                    commitment: type === 'audit' ? undefined : 6 
                  }
                }));
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stratos-gold"
            >
              <option value="audit">Audit 360° Complet - 4 000€ (forfait)</option>
              <option value="standard">Standard - 1 000€/mois</option>
              <option value="premium">Premium - 2 000€/mois</option>
              <option value="signature">Signature - 3 500€/mois</option>
            </select>

            {contractData.formule.type !== 'audit' && (
              <>
                <select
                  value={contractData.formule.commitment}
                  onChange={(e) => {
                    const commitment = parseInt(e.target.value) as 6 | 12;
                    setContractData(prev => ({
                      ...prev,
                      formule: { ...prev.formule, commitment },
                      loyaltyDiscount: commitment === 12
                    }));
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stratos-gold"
                >
                  <option value="6">Engagement 6 mois</option>
                  <option value="12">Engagement 12 mois (-15%)</option>
                </select>

                <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="loyalty"
                    checked={contractData.loyaltyDiscount && contractData.formule.commitment === 12}
                    onChange={(e) => setContractData(prev => ({ 
                      ...prev, 
                      loyaltyDiscount: e.target.checked && prev.formule.commitment === 12
                    }))}
                    disabled={contractData.formule.commitment !== 12}
                    className="w-4 h-4 text-stratos-gold rounded"
                  />
                  <label htmlFor="loyalty" className="text-sm text-gray-700">
                    Remise fidélité -15%
                  </label>
                </div>
              </>
            )}
          </div>

          {/* Audit Complet */}
          {contractData.formule.type !== 'audit' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Audit 360° Complet Initial (optionnel)
              </h3>
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={contractData.auditComplet?.included || false}
                      onChange={(e) => setContractData(prev => ({
                        ...prev,
                        auditComplet: {
                          included: e.target.checked,
                          offered: e.target.checked ? (prev.auditComplet?.offered || false) : false
                        }
                      }))}
                      className="w-4 h-4 text-stratos-gold rounded"
                    />
                    <span className="text-sm font-medium text-gray-900">
                      Inclure l'Audit 360° Complet (4 000€ HT)
                    </span>
                  </label>
                  
                  {contractData.auditComplet?.included && (
                    <label className="flex items-center gap-2 cursor-pointer ml-6">
                      <input
                        type="checkbox"
                        checked={contractData.auditComplet?.offered || false}
                        onChange={(e) => setContractData(prev => ({
                          ...prev,
                          auditComplet: {
                            ...prev.auditComplet!,
                            offered: e.target.checked
                          }
                        }))}
                        className="w-4 h-4 text-green-600 rounded"
                      />
                      <span className="text-sm font-medium text-green-700">
                        Offrir l'audit complet (valeur 4 000€)
                      </span>
                    </label>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4 border-t">
            <Button onClick={() => window.print()}>
              <Printer className="w-4 h-4 mr-2" />
              Imprimer
            </Button>
            <Button variant="outline" onClick={handleDownloadPDF} disabled={isGenerating}>
              <Download className="w-4 h-4 mr-2" />
              {isGenerating ? 'Génération...' : 'Télécharger PDF'}
            </Button>
          </div>
          {isGenerating && (
            <p className="text-sm text-blue-600 mt-3 animate-pulse">
              ⏳ Génération du PDF en cours... Veuillez patienter.
            </p>
          )}
        </div>
      </Card>

      {/* Contract Document */}
      <div ref={documentRef}>
        <Card className="p-12 bg-white print:shadow-none print:border-0 max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8 pb-6 border-b-2 border-stratos-gold">
            <h1 
              className="text-5xl font-bold mb-2"
              style={{
                background: 'linear-gradient(to right, #E9BC66, #F4D89E)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              STRATOS
            </h1>
            <p className="text-xl text-gray-600 font-light mb-4">Hospitality</p>
            <h2 className="text-2xl font-bold text-gray-900 mt-6">
              CONTRAT EXPRESS DE PRESTATION
            </h2>
            <p className="text-sm text-gray-600 mt-2">
              Version 2025 – Document abrégé de pré-engagement
            </p>
            <p className="text-sm text-gray-500 mt-4">
              Contrat n° {contractData.contractNumber}
            </p>
          </div>

          {/* Article 1 - Objet */}
          <section className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              1. OBJET DU CONTRAT
            </h3>
            <p className="text-sm text-gray-700 leading-relaxed">
              Le présent document constitue un accord d'intention entre <strong>STRATOS Hospitality</strong> 
              (ci-après "STRATOS Hospitality") et le Client ci-dessous désigné, en vue de la réalisation 
              d'une mission de conseil, d'audit et/ou d'accompagnement stratégique selon la formule choisie.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed mt-3">
              Il formalise les conditions essentielles avant la signature du <strong>Contrat cadre STRATOS 
              Hospitality 2025</strong>.
            </p>
          </section>

          {/* Article 2 - Formules */}
          <section className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              2. FORMULES DISPONIBLES
            </h3>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-100 border-b-2 border-stratos-gold">
                    <th className="text-left p-3 font-semibold text-gray-700">Formule</th>
                    <th className="text-center p-3 font-semibold text-gray-700">Tarif HT</th>
                    <th className="text-center p-3 font-semibold text-gray-700">Durée engagement</th>
                    <th className="text-center p-3 font-semibold text-gray-700">Condition règlement</th>
                    <th className="text-center p-3 font-semibold text-gray-700">Avantage fidélité (12 mois)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-3 font-medium text-gray-900">Audit 360° Complet</td>
                    <td className="p-3 text-center text-gray-900">4 000 € (forfait)</td>
                    <td className="p-3 text-center text-gray-700">—</td>
                    <td className="p-3 text-center text-gray-700">30 % signature, 70 % livraison</td>
                    <td className="p-3 text-center text-gray-700">—</td>
                  </tr>
                  <tr className="border-b bg-blue-50">
                    <td className="p-3 font-medium text-gray-900">Standard</td>
                    <td className="p-3 text-center text-gray-900">1 000 €/mois</td>
                    <td className="p-3 text-center text-gray-700">6 ou 12 mois</td>
                    <td className="p-3 text-center text-gray-700">1er mois (6 mois) / 2 mois (12 mois)</td>
                    <td className="p-3 text-center text-green-700 font-semibold">–15 % sur 12 mois</td>
                  </tr>
                  <tr className="border-b bg-purple-50">
                    <td className="p-3 font-medium text-gray-900">Premium</td>
                    <td className="p-3 text-center text-gray-900">2 000 €/mois</td>
                    <td className="p-3 text-center text-gray-700">6 ou 12 mois</td>
                    <td className="p-3 text-center text-gray-700">1er mois (6 mois) / 2 mois (12 mois)</td>
                    <td className="p-3 text-center text-green-700 font-semibold">–15 % sur 12 mois</td>
                  </tr>
                  <tr className="border-b bg-yellow-50">
                    <td className="p-3 font-medium text-gray-900">Signature</td>
                    <td className="p-3 text-center text-gray-900">3 500 €/mois</td>
                    <td className="p-3 text-center text-gray-700">6 ou 12 mois</td>
                    <td className="p-3 text-center text-gray-700">1er mois (6 mois) / 2 mois (12 mois)</td>
                    <td className="p-3 text-center text-green-700 font-semibold">–15 % sur 12 mois</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-xs text-gray-600 mt-4 italic">
              Les prestations sont facturées mensuellement à terme à échoir, par <strong>prélèvement SEPA automatique</strong>.
            </p>
          </section>

          {/* Article 3 - Conditions essentielles */}
          <section className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              3. CONDITIONS ESSENTIELLES
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>Engagement ferme minimal :</strong> 6 mois, renouvelable par tacite reconduction 
                  par périodes de six (6) mois.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>Résiliation à l'échéance :</strong> préavis de 30 jours avant renouvellement.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>Paiement :</strong> mensuel par prélèvement SEPA automatique, ou règlement immédiat 
                  du premier ou des deux premiers mois selon la durée choisie.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>Responsabilité :</strong> limitée au montant HT du contrat.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>Confidentialité :</strong> toutes les informations échangées sont confidentielles 
                  pour une durée de 5 ans.
                </span>
              </li>
            </ul>
          </section>

          {/* Article 4 - Programme Parrainage */}
          <section className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              4. PROGRAMME DE PARRAINAGE STRATOS HOSPITALITY
            </h3>
            <p className="text-sm text-gray-700 mb-3">
              Chaque Client peut recommander un nouveau partenaire ("Filleul") et bénéficier d'un 
              <strong> crédit à valoir sur ses factures</strong> :
            </p>
            <ul className="space-y-2 text-sm text-gray-700 ml-4">
              <li className="flex items-start gap-2">
                <span className="font-bold text-stratos-gold">•</span>
                <span>
                  <strong>5 % HT</strong> du montant du contrat du Filleul, ou <strong>10 % HT</strong> 
                  si le contrat du Parrain est prolongé à 12 mois.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-stratos-gold">•</span>
                <span>
                  Le crédit est valable pendant la durée du contrat et au maximum 12 mois, puis remis 
                  à zéro à l'issue du contrat semestriel ou annuel.
                </span>
              </li>
            </ul>

            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-xs text-yellow-900">
                <strong>Exemple :</strong> Si vous parrainez un restaurant qui souscrit un Pack Premium 
                (2 000€/mois sur 12 mois = 24 000€), et que votre contrat est également sur 12 mois, 
                vous bénéficiez d'un crédit de <strong>2 400€ HT</strong> (10% de 24 000€) à déduire 
                de vos factures.
              </p>
            </div>
          </section>

          {/* Article 5 - Clause d'acceptation */}
          <section className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              5. CLAUSE D'ACCEPTATION
            </h3>
            <p className="text-sm text-gray-700 leading-relaxed">
              Le Client reconnaît avoir pris connaissance et accepté sans réserve les <strong>Conditions 
              Générales de Prestations de Services STRATOS Hospitality (V5.2 – 2025)</strong>, l'<strong>Annexe 
              RGPD Light</strong> et l'<strong>Annexe Avantage Fidélité & Parrainage STRATOS Hospitality</strong>, 
              qui font partie intégrante du présent accord.
            </p>
          </section>

          {/* Article 6 - Validité */}
          <section className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              6. VALIDITÉ
            </h3>
            <p className="text-sm text-gray-700 leading-relaxed mb-3">
              Ce document exprime la volonté des parties de collaborer et vaut <strong>pré-engagement 
              contractuel</strong>.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              La mission prendra effet à la signature du <strong>Contrat cadre STRATOS Hospitality 2025</strong>.
            </p>
          </section>

          {/* Formule choisie - Récapitulatif */}
          <div className="mb-8 p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-300 rounded-xl">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              📋 FORMULE CHOISIE
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600 mb-1">Formule</p>
                <p className="font-bold text-gray-900 text-lg">
                  {FORMULES[contractData.formule.type].name}
                </p>
              </div>
              {contractData.formule.type !== 'audit' && (
                <>
                  <div>
                    <p className="text-gray-600 mb-1">Engagement</p>
                    <p className="font-bold text-gray-900 text-lg">
                      {contractData.formule.commitment} mois
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600 mb-1">Tarif mensuel HT</p>
                    <p className="font-bold text-blue-900 text-lg">
                      {pricing.monthlyHT.toLocaleString()}€
                      {contractData.loyaltyDiscount && (
                        <Badge variant="success" className="ml-2">-15%</Badge>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600 mb-1">Total engagement HT</p>
                    <p className="font-bold text-gray-900 text-lg">
                      {pricing.totalHT.toLocaleString()}€
                    </p>
                  </div>
                </>
              )}
              {contractData.formule.type === 'audit' && (
                <div>
                  <p className="text-gray-600 mb-1">Tarif forfaitaire HT</p>
                  <p className="font-bold text-blue-900 text-lg">4 000€</p>
                  <p className="text-xs text-gray-600 mt-1">30% signature + 70% livraison</p>
                </div>
              )}
            </div>
            
            {/* Audit inclus en bonus */}
            {contractData.auditComplet?.included && contractData.formule.type !== 'audit' && (
              <div className="mt-4 pt-4 border-t border-blue-300">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">
                      + Audit 360° Complet Initial
                      {contractData.auditComplet.offered && (
                        <Badge variant="success" className="ml-2">OFFERT</Badge>
                      )}
                    </p>
                    <p className="text-xs text-gray-600">
                      Crawl technique, analyse concurrentielle, roadmap 6 mois
                    </p>
                  </div>
                  <p className="font-bold text-lg">
                    {contractData.auditComplet.offered ? (
                      <span className="text-green-600">OFFERT</span>
                    ) : (
                      <span className="text-blue-900">+4 000€</span>
                    )}
                  </p>
                </div>
                {contractData.auditComplet.offered && (
                  <p className="text-xs text-green-700 font-semibold mt-2 italic">
                    Offert par Stratos Hospitality (valeur commerciale : 4 000€)
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Important Notice */}
          <div className="mb-8 p-6 bg-yellow-50 border-2 border-yellow-300 rounded-xl">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-900">
                <p className="font-bold mb-2">⚠️ Document de pré-engagement</p>
                <p>
                  Ce document formalise un accord d'intention. La mission débutera à la signature 
                  du <strong>Contrat cadre STRATOS Hospitality 2025</strong>.
                </p>
              </div>
            </div>
          </div>

          {/* Article 7 - Signatures */}
          <section className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6">
              7. SIGNATURES
            </h3>
            
            <div className="grid grid-cols-2 gap-8 text-sm">
              <div className="space-y-4">
                <div>
                  <p className="text-gray-600 mb-1">Client</p>
                  <div className="border-b border-gray-300 pb-1">
                    {contractData.client.companyName || '__________________________________'}
                  </div>
                </div>
                <div>
                  <p className="text-gray-600 mb-1">Nom / Raison sociale</p>
                  <div className="border-b border-gray-300 pb-1">
                    {contractData.client.companyName || '__________________________________'}
                  </div>
                </div>
                <div>
                  <p className="text-gray-600 mb-1">Fonction</p>
                  <div className="border-b border-gray-300 pb-1">
                    {contractData.client.title || '__________________________________'}
                  </div>
                </div>
                <div className="pt-8">
                  <p className="text-gray-600 mb-1">Signature</p>
                  <div className="h-20 border-b border-gray-300"></div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-gray-600 mb-1">STRATOS Hospitality</p>
                  <div className="border-b border-gray-300 pb-1">
                    STRATOS Hospitality
                  </div>
                </div>
                <div>
                  <p className="text-gray-600 mb-1">Nom</p>
                  <div className="border-b border-gray-300 pb-1">
                    Adrien Cerdan
                  </div>
                </div>
                <div>
                  <p className="text-gray-600 mb-1">Fonction</p>
                  <div className="border-b border-gray-300 pb-1">
                    Directeur
                  </div>
                </div>
                <div className="pt-8">
                  <p className="text-gray-600 mb-1">Signature</p>
                  <div className="h-20 border-b border-gray-300"></div>
                </div>
              </div>
            </div>

            <div className="mt-8 text-center text-sm text-gray-700">
              <p>
                Fait à <span className="border-b border-gray-300 px-2">{contractData.location}</span>, 
                le {new Date(contractData.date).toLocaleDateString('fr-FR')}
              </p>
            </div>
          </section>

          {/* Encadré important */}
          <div className="mt-8 p-6 bg-gray-900 text-white rounded-lg border-2 border-stratos-gold">
            <p className="text-center text-sm leading-relaxed">
              <strong className="text-stratos-gold">Ce document formalise un accord d'intention.</strong>
              <br />
              La mission débutera à la signature du Contrat cadre STRATOS Hospitality 2025.
            </p>
          </div>

          {/* Footer */}
          <div className="mt-12 pt-6 border-t text-center text-xs text-gray-500 space-y-1">
            <p><strong>STRATOS Hospitality</strong> (by Getup Agency) – SARL au capital de 150 500€</p>
            <p>10 impasse de l'Olivaie, 83210 La Farlède</p>
            <p>SIRET : 822 096 913 00022 – TVA : FR82822096913</p>
            <p className="mt-2">Représentée par Adrien Cerdan</p>
            <p className="mt-4">© STRATOS Hospitality 2025 – Tous droits réservés</p>
          </div>
        </Card>
      </div>
    </div>
  );
};

